import { getFootprintSquares } from '../../../components/Zombies/utils/pushGeometryCore.mjs';
import { stepCrossesWalls } from '../../mapLayout.mjs';
const key = point => `${point.x},${point.y}`;
const adjacent = (a,b) => Math.max(Math.abs(a.x-b.x),Math.abs(a.y-b.y)) === 1;
const cells = (point,size) => Array.from({length:size*size},(_,i)=>({x:point.x+i%size,y:point.y+Math.floor(i/size)}));
const valid = p => Number.isInteger(p?.x)&&Number.isInteger(p?.y);
// `edges` are wall edges along grid lines that stop movement ("x,y,h" runs along the top of square x,y; "x,y,v" down its
// left side), from a map's drawn layout; how a step crosses them is mapLayout's stepCrossesWalls. Every square of the
// footprint (and of anyone carried) is checked.
// `fears` are the sources of a Frightened mover's fear ({ position, size }): no step may bring it closer to one (2024).
const squaresBetween = (a, aSize, b, bSize) => Math.max(
  Math.max(0, b.x - (a.x + aSize - 1), a.x - (b.x + bSize - 1)),
  Math.max(0, b.y - (a.y + aSize - 1), a.y - (b.y + bSize - 1)));
export function movementSearch({ origin, columns, rows, size='medium', occupants=[], blocked=[], difficult=[], thorns=[], edges=[], remainingFeet=0, crawl=false, climb=false, direction=null, passengers=[], dragging=false, fears=[] }) {
  if(!valid(origin)||!Number.isInteger(columns)||!Number.isInteger(rows)||columns<1||rows<1||columns*rows>40000) return new Map();
  const width=getFootprintSquares(size),walls=new Set(blocked.map(key)),terrain=new Set(difficult.map(key)),brambles=new Set(thorns.map(key)),edgeWalls=new Set(edges);
  const stepCrossesWall=(from,dx,dy)=>stepCrossesWalls(edgeWalls,
    [...cells(from,width),...passengers.flatMap(p=>cells({x:from.x+p.dx,y:from.y+p.dy},getFootprintSquares(p.size)))],dx,dy);
  const occupied=occupants.map(item=>({...item,cells:new Set(cells(item.position,getFootprintSquares(item.size)).map(key))}));
  const assess=point=>{
    const footprint=[...cells(point,width),...passengers.flatMap(p=>cells({x:point.x+p.dx,y:point.y+p.dy},getFootprintSquares(p.size)))];
    if(footprint.some(p=>p.x<0||p.y<0||p.x>=columns||p.y>=rows||walls.has(key(p))))return null;
    const crossed=occupied.filter(item=>footprint.some(p=>item.cells.has(key(p))));
    if(crossed.some(item=>!item.passable))return null;
    const costly=footprint.some(p=>terrain.has(key(p)))||crossed.some(item=>!item.ally&&item.size!=='tiny');
    // Wall of Thorns: every foot through it costs 4.
    const brambled=footprint.some(p=>brambles.has(key(p)));
    return {cost:5+(brambled?15:costly?5:0)+(crawl?5:0)+(climb?5:0)+(dragging?5:0),stop:crossed.length===0};
  };
  const result=new Map([[key(origin),{point:origin,cost:0,routeLength:0,path:[origin],stop:true}]]),queue=[result.get(key(origin))];
  while(queue.length){
    queue.sort((a,b)=>a.cost-b.cost||a.routeLength-b.routeLength);const current=queue.shift();
    if(current!==result.get(key(current.point)))continue;
    for(let dx=-1;dx<=1;dx++)for(let dy=-1;dy<=1;dy++){
      if(!dx&&!dy||direction&&(dx!==direction.x||dy!==direction.y))continue;
      if(stepCrossesWall(current.point,dx,dy))continue;
      const next={x:current.point.x+dx,y:current.point.y+dy},info=assess(next);
      if(fears.some(f=>valid(f?.position)&&squaresBetween(next,width,f.position,getFootprintSquares(f.size))<squaresBetween(current.point,width,f.position,getFootprintSquares(f.size))))continue;
      if(!info||dx&&dy&&(!assess({x:current.point.x+dx,y:current.point.y})||!assess({x:current.point.x,y:current.point.y+dy})))continue;
      // Diagonals and straight steps spend the same feet, but prefer the
      // geometrically shorter route when their movement costs tie.
      const cost=current.cost+info.cost,routeLength=current.routeLength+Math.hypot(dx,dy),previous=result.get(key(next));
      if(cost>remainingFeet||previous&&(cost>previous.cost||cost===previous.cost&&routeLength>=previous.routeLength-1e-9))continue;
      const record={point:next,cost,routeLength,path:[...current.path,next],stop:info.stop};result.set(key(next),record);queue.push(record);
    }
  }
  return result;
}
export function validateMovementPath(options,path) {
  if(!Array.isArray(path)||path.length<2||path.length>512||path.some(p=>!valid(p))||key(path[0])!==key(options.origin))throw new Error('Invalid movement path.');
  let cost=0;
  for(let i=1;i<path.length;i++){
    if(!adjacent(path[i-1],path[i]))throw new Error('Movement path must use adjacent squares.');
    const step=movementSearch({...options,origin:path[i-1],remainingFeet:25}).get(key(path[i]));
    if(!step||step.path.length!==2)throw new Error('Movement path is blocked.');
    cost+=step.cost;if(i===path.length-1&&!step.stop)throw new Error('Cannot stop in an occupied space.');
  }
  if(cost>options.remainingFeet)throw new Error('Insufficient movement remaining.');
  return {cost,distance:5*(path.length-1)};
}
